import { useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import type { EventDiff, EventDiffItem } from '@/types'

const APP_TYPE = 'tenant'
const USER_TYPE = 'user'

export interface EventIdentityGroups {
  added: EventDiffItem[]
  removed: EventDiffItem[]
  unchanged: EventDiffItem[]
}

function byEvent(a: EventDiffItem, b: EventDiffItem) {
  const nameA = (a.name?.trim() || a.event_code).toLocaleLowerCase()
  const nameB = (b.name?.trim() || b.event_code).toLocaleLowerCase()
  const byName = nameA.localeCompare(nameB)
  if (byName !== 0) return byName
  return a.event_code.localeCompare(b.event_code)
}

function groupsOf(
  items: EventDiffItem[],
  subscribeType: 'tenant' | 'user',
): EventDiffItem[] {
  return items
    .filter((item) => (item.subscribe_type ?? APP_TYPE) === subscribeType)
    .sort(byEvent)
}

/** 按身份拆 diff。缺 subscribe_type 的条目归入应用身份事件 */
export function splitEventDiff(diff: EventDiff): {
  app: EventIdentityGroups
  user: EventIdentityGroups
} {
  const take = (subscribeType: 'tenant' | 'user'): EventIdentityGroups => ({
    added: groupsOf(diff.added, subscribeType),
    removed: groupsOf(diff.removed, subscribeType),
    unchanged: groupsOf(diff.unchanged_items, subscribeType),
  })
  return { app: take(APP_TYPE), user: take(USER_TYPE) }
}

function hasAny(groups: EventIdentityGroups) {
  return (
    groups.added.length > 0 ||
    groups.removed.length > 0 ||
    groups.unchanged.length > 0
  )
}

/** content：有可展示事件；none：两版都没有事件，整块不展示 */
export function eventPanelMode(diff: EventDiff): 'content' | 'none' {
  const { app, user } = splitEventDiff(diff)
  return hasAny(app) || hasAny(user) ? 'content' : 'none'
}

/** 草稿抽屉里的事件对比。放在权限下方，展示规则对齐权限块 */
export default function EventsSection({ diff }: { diff: EventDiff }) {
  const { t } = useTranslation()
  const { app, user } = splitEventDiff(diff)

  if (eventPanelMode(diff) === 'none') return null

  const showApp = hasAny(app)
  const showUser = hasAny(user)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {showApp && (
        <IdentityBlock
          title={t('versionForm.eventApp')}
          sameAsPrev={app.added.length === 0 && app.removed.length === 0}
          groups={app}
        />
      )}
      {showApp && showUser && <div style={dashStyle} />}
      {showUser && (
        <IdentityBlock
          title={t('versionForm.eventUser')}
          sameAsPrev={user.added.length === 0 && user.removed.length === 0}
          groups={user}
        />
      )}
    </div>
  )
}

function IdentityBlock({
  title,
  sameAsPrev,
  groups,
}: {
  title: string
  sameAsPrev: boolean
  groups: EventIdentityGroups
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 500, lineHeight: '22px', color: '#000' }}>
        {title}
      </div>
      {sameAsPrev && (
        <div style={{ fontSize: 13, lineHeight: '20px', color: 'rgba(0,0,0,0.6)' }}>
          {t('versionForm.permSameAsPrev')}
        </div>
      )}
      {groups.added.length > 0 && (
        <ChangeGroup
          label={t('versionForm.eventAddedGroup', { count: groups.added.length })}
          tone="added"
          items={groups.added}
        />
      )}
      {groups.removed.length > 0 && (
        <ChangeGroup
          label={t('versionForm.eventRemovedGroup', { count: groups.removed.length })}
          tone="removed"
          items={groups.removed}
        />
      )}
      {groups.unchanged.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={groupLabelStyle}>
              {t('versionForm.eventUnchangedGroup', { count: groups.unchanged.length })}
            </span>
            <button
              type="button"
              aria-expanded={open}
              aria-label={open ? t('versionForm.permCollapse') : t('versionForm.permViewAll')}
              onClick={() => setOpen((value) => !value)}
              style={toggleStyle}
            >
              <span style={open ? caretUpStyle : caretDownStyle} />
            </button>
          </div>
          {open && <NameList items={groups.unchanged} />}
        </div>
      )}
    </div>
  )
}

const TAG_TONE: Record<'added' | 'removed', CSSProperties> = {
  added: { background: 'rgba(0, 180, 42, 0.08)', color: '#009726' },
  removed: { background: 'rgba(255, 56, 60, 0.08)', color: '#FF2929' },
}

function ChangeGroup({
  label,
  tone,
  items,
}: {
  label: string
  tone: 'added' | 'removed'
  items: EventDiffItem[]
}) {
  const { t } = useTranslation()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={groupLabelStyle}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span
          style={{
            flexShrink: 0,
            padding: '0 12px',
            borderRadius: 999,
            fontSize: 12,
            lineHeight: '18px',
            ...TAG_TONE[tone],
          }}
        >
          {t(tone === 'added' ? 'versionForm.scopeAdded' : 'versionForm.scopeRemoved')}
        </span>
        <NameList items={items} />
      </div>
    </div>
  )
}

function NameList({ items }: { items: EventDiffItem[] }) {
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: 13,
        lineHeight: '20px',
        color: '#000',
        wordBreak: 'break-word',
      }}
    >
      {items.map((item, index) => {
        const label = item.name?.trim() || item.event_code
        return (
          <span key={`${item.subscribe_type ?? APP_TYPE}:${item.event_code}`}>
            {index > 0 ? ', ' : null}
            <Tooltip title={item.event_code}>
              <span>{label}</span>
            </Tooltip>
          </span>
        )
      })}
    </span>
  )
}

const groupLabelStyle: CSSProperties = {
  fontSize: 13,
  lineHeight: '20px',
  color: 'rgba(0,0,0,0.6)',
}

const dashStyle: CSSProperties = {
  height: 1,
  borderBottom: '1px dashed #DCDCDC',
}

const toggleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  padding: 0,
  flexShrink: 0,
  borderRadius: 6,
  border: '1px solid #DCDCDC',
  background: 'rgba(255, 255, 255, 0.85)',
  cursor: 'pointer',
}

const caretDownStyle: CSSProperties = {
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderTop: '5px solid #000',
}

const caretUpStyle: CSSProperties = {
  width: 0,
  height: 0,
  borderLeft: '4px solid transparent',
  borderRight: '4px solid transparent',
  borderBottom: '5px solid #000',
}
